<template>
  <section class="usage-manual-page">
    <div class="manual-hero card border-0 shadow-sm mb-3">
      <div class="card-body">
        <div class="manual-hero-content">
          <div>
            <span class="manual-eyebrow">{{ eyebrow }}</span>
            <h1 class="h3 mb-2">{{ title }}</h1>
            <p class="text-muted mb-0">{{ subtitle }}</p>
          </div>

          <div class="manual-hero-actions">
            <div class="manual-search">
              <i class="fa fa-search"></i>
              <PrimevueInputText
                v-model.trim="search"
                type="search"
                class="w-100"
                placeholder="Rechercher une fonctionnalité..."
              />
            </div>

            <button
              type="button"
              class="btn btn-primary btn-sm btn-icon-label manual-print-button"
              @click="printManual"
            >
              <i class="fa fa-print"></i>
              <span>Imprimer / Exporter PDF</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="manual-summary mb-3">
      <span>
        <strong>{{ filteredItems.length }}</strong>
        {{ filteredItems.length > 1 ? 'chapitres affichés' : 'chapitre affiché' }}
      </span>
      <button
        v-if="search"
        type="button"
        class="btn btn-outline-secondary btn-sm"
        @click="search = ''"
      >
        <i class="fa fa-xmark me-1"></i>
        Réinitialiser
      </button>
    </div>

    <section
      v-if="showTableOfContents && showTocCard && filteredItems.length"
      class="manual-toc"
      aria-labelledby="manual-toc-title"
    >
      <div class="manual-toc-heading">
        <div>
          <span class="manual-toc-kicker">Sommaire</span>
          <h2 id="manual-toc-title" class="h5 mb-0">Parcourir le guide</h2>
        </div>
        <span class="manual-toc-count">
          {{ tocGroups.length }}
          {{ tocGroups.length > 1 ? 'parties' : 'partie' }}
        </span>
      </div>

      <div class="manual-toc-grid">
        <div
          v-for="group in tocGroups"
          :key="group.module"
          class="manual-toc-group"
        >
          <strong>{{ group.module }}</strong>
          <ol>
            <li
              v-for="item in group.items"
              :key="`toc-${item.id}`"
            >
              <a
                :href="`#${item.id}`"
                @click="activeItemId = item.id"
              >
                {{ item.title }}
              </a>
            </li>
          </ol>
        </div>
      </div>
    </section>

    <div class="manual-doc-layout">
      <aside v-if="showTableOfContents && filteredItems.length" class="manual-sidebar" aria-label="Navigation du guide">
        <div class="manual-sidebar-inner">
          <span class="manual-sidebar-title">Sommaire</span>
          <nav>
            <a
              v-for="item in filteredItems"
              :key="`nav-${item.id}`"
              :href="`#${item.id}`"
              class="manual-sidebar-link"
              :class="{ active: activeItemId === item.id }"
              @click="activeItemId = item.id"
            >
              <small>{{ item.module }}</small>
              <span>{{ item.title }}</span>
            </a>
          </nav>
        </div>
      </aside>

      <div class="manual-content">
        <div v-if="!filteredItems.length" class="manual-empty card border-0 shadow-sm">
          <div class="card-body">
            <i class="fa fa-book-open"></i>
            <strong>Aucun résultat</strong>
            <span>Essayez avec un module, un chemin ou une action plus simple.</span>
          </div>
        </div>

        <div v-else class="manual-grid">
          <article
            v-for="item in filteredItems"
            :id="item.id"
            :key="item.id"
            class="card border-0 shadow-sm manual-card"
          >
            <div class="card-body">
              <div class="manual-card-header">
                <span class="manual-module-badge">{{ item.module }}</span>
                <router-link
                  v-if="canOpenRoute(item.routeName)"
                  class="btn btn-outline-primary btn-sm btn-icon-label"
                  :to="{ name: item.routeName }"
                >
                  <i class="fa fa-arrow-up-right-from-square"></i>
                  <span>Ouvrir</span>
                </router-link>
              </div>

              <h2 class="h4 mb-2">{{ item.title }}</h2>

              <div v-if="item.path" class="manual-path mb-3">
                <i class="fa fa-location-dot"></i>
                <span>{{ item.path }}</span>
              </div>

              <p v-if="item.summary" class="manual-summary-text">{{ item.summary }}</p>

              <div v-if="item.objective" class="manual-section">
                <h3>Objectif</h3>
                <p>{{ item.objective }}</p>
              </div>

              <div v-if="item.scenario || item.example" class="manual-info-block scenario">
                <strong>
                  <i class="fa fa-circle-play"></i>
                  Scénario métier
                </strong>
                <p>{{ item.scenario || item.example }}</p>
              </div>

              <div v-if="item.steps?.length" class="manual-section">
                <h3>Étapes à suivre</h3>
                <ol class="manual-steps">
                  <li v-for="step in item.steps" :key="step">{{ step }}</li>
                </ol>
              </div>

              <div v-if="item.impacts?.length" class="manual-section">
                <h3>Impact sur votre activité</h3>
                <ul class="manual-list">
                  <li v-for="impact in item.impacts" :key="impact">{{ impact }}</li>
                </ul>
              </div>

              <div v-if="item.commonErrors?.length" class="manual-section">
                <h3>Erreurs fréquentes</h3>
                <ul class="manual-list">
                  <li v-for="error in item.commonErrors" :key="error">{{ error }}</li>
                </ul>
              </div>

              <div v-if="item.callouts?.length || item.advice" class="manual-callout-grid">
                <div
                  v-for="callout in normalizedCallouts(item)"
                  :key="`${item.id}-${callout.type}-${callout.title}`"
                  class="manual-callout"
                  :class="`manual-callout-${callout.type}`"
                >
                  <strong>
                    <i :class="calloutIcon(callout.type)"></i>
                    {{ calloutLabel(callout) }}
                  </strong>
                  <p>{{ callout.text }}</p>
                </div>
              </div>

              <div v-if="item.figures?.length" class="manual-figures">
                <figure
                  v-for="figure in item.figures"
                  :key="figure.src"
                  class="manual-figure"
                >
                  <div class="manual-figure-frame">
                    <img :src="figure.src" :alt="figure.alt || figure.caption" loading="lazy" />
                    <span
                      v-for="annotation in figure.annotations || []"
                      :key="`${figure.src}-${annotation.label}`"
                      class="manual-figure-marker"
                      :style="{ left: `${annotation.x}%`, top: `${annotation.y}%` }"
                      :title="annotation.text"
                    >
                      {{ annotation.label }}
                    </span>
                  </div>
                  <figcaption>
                    <strong>{{ figure.caption }}</strong>
                    <span v-if="figure.description">{{ figure.description }}</span>
                  </figcaption>
                  <ol v-if="figure.annotations?.length" class="manual-figure-notes">
                    <li v-for="annotation in figure.annotations" :key="`${figure.src}-note-${annotation.label}`">
                      <strong>{{ annotation.label }}.</strong>
                      {{ annotation.text }}
                    </li>
                  </ol>
                </figure>
              </div>

              <div v-if="item.subsections?.length" class="manual-subsections">
                <section v-for="section in item.subsections" :key="section.title" class="manual-subsection">
                  <h3>{{ section.title }}</h3>
                  <p v-if="section.text">{{ section.text }}</p>
                  <ol v-if="section.steps?.length" class="manual-steps">
                    <li v-for="step in section.steps" :key="step">{{ step }}</li>
                  </ol>
                  <ul v-if="section.items?.length" class="manual-list">
                    <li v-for="entry in section.items" :key="entry">{{ entry }}</li>
                  </ul>
                </section>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>
    <div class="manual-powered-by">powered by Suite241</div>
  </section>
</template>

<script>
import { hasAllPermissions, hasPermission, routePermission, routePermissions } from '@/support/permissions';

export default {
  name: 'UsageManualPage',
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: true,
    },
    eyebrow: {
      type: String,
      default: 'Manuel d’utilisation',
    },
    items: {
      type: Array,
      required: true,
    },
    showTocCard: {
      type: Boolean,
      default: true,
    },
    showTableOfContents: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      search: '',
      activeItemId: '',
      printCleanupTimer: null,
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters['auth/currentUser'];
    },
    manualItems() {
      return this.items.map((item, index) => ({
        ...item,
        id: this.manualItemId(item, index),
      }));
    },
    filteredItems() {
      const query = this.normalize(this.search);
      if (!query) return this.manualItems;

      return this.manualItems.filter((item) => this.collectSearchText(item).includes(query));
    },
    tocGroups() {
      return this.filteredItems.reduce((groups, item) => {
        const existingGroup = groups.find((group) => group.module === item.module);

        if (existingGroup) {
          existingGroup.items.push(item);
          return groups;
        }

        groups.push({
          module: item.module,
          items: [item],
        });

        return groups;
      }, []);
    },
  },
  watch: {
    filteredItems: {
      immediate: true,
      handler(items) {
        this.activeItemId = items[0]?.id || '';
      },
    },
  },
  beforeUnmount() {
    this.cleanupPrintMode();
  },
  methods: {
    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    },
    collectSearchText(value) {
      const chunks = [];
      const visit = (entry) => {
        if (!entry) return;
        if (typeof entry === 'string' || typeof entry === 'number') {
          chunks.push(String(entry));
          return;
        }
        if (Array.isArray(entry)) {
          entry.forEach(visit);
          return;
        }
        if (typeof entry === 'object') {
          Object.values(entry).forEach(visit);
        }
      };

      visit(value);
      return this.normalize(chunks.join(' '));
    },
    manualItemId(item, index) {
      const slug = this.normalize(`${item.module}-${item.title}`)
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      return `manuel-${slug || index}`;
    },
    normalizedCallouts(item) {
      const callouts = Array.isArray(item.callouts) ? [...item.callouts] : [];
      if (item.advice) {
        callouts.push({
          type: 'tip',
          title: 'Conseil de gestion',
          text: item.advice,
        });
      }

      return callouts;
    },
    calloutLabel(callout) {
      if (callout.title) return callout.title;
      if (callout.type === 'warning') return 'Attention';
      if (callout.type === 'know') return 'Bon à savoir';
      return 'Astuce';
    },
    calloutIcon(type) {
      if (type === 'warning') return 'fa fa-triangle-exclamation';
      if (type === 'know') return 'fa fa-circle-info';
      return 'fa fa-lightbulb';
    },
    printManual() {
      document.body.classList.add('suite241-printing-usage-manual');
      window.addEventListener('afterprint', this.cleanupPrintMode, { once: true });
      window.print();
      this.printCleanupTimer = window.setTimeout(this.cleanupPrintMode, 1200);
    },
    cleanupPrintMode() {
      document.body.classList.remove('suite241-printing-usage-manual');
      window.removeEventListener('afterprint', this.cleanupPrintMode);
      if (this.printCleanupTimer) {
        window.clearTimeout(this.printCleanupTimer);
        this.printCleanupTimer = null;
      }
    },
    canOpenRoute(routeName) {
      if (!routeName || !this.$router.hasRoute(routeName)) return false;

      const resolved = this.$router.resolve({ name: routeName });
      const allowedRoles = Array.isArray(resolved.meta?.allowedRoles) ? resolved.meta.allowedRoles : [];
      const permission = routePermission(resolved);
      const permissions = routePermissions(resolved);
      const userRole = this.currentUser?.role;

      if (allowedRoles.length && userRole && !allowedRoles.includes(userRole) && !hasPermission(this.currentUser, permission)) {
        return false;
      }

      return hasPermission(this.currentUser, permission) && hasAllPermissions(this.currentUser, permissions);
    },
  },
};
</script>

<style scoped>
.usage-manual-page {
  display: grid;
  gap: 0.25rem;
}

.manual-hero {
  background:
    linear-gradient(135deg, rgba(13, 148, 136, 0.1), rgba(37, 99, 235, 0.08)),
    var(--suite241-card-bg, #ffffff);
}

.manual-hero-content {
  align-items: center;
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 460px);
}

.manual-hero-actions {
  display: grid;
  gap: 0.75rem;
}

.manual-print-button {
  justify-self: end;
}

.manual-eyebrow {
  color: #0d9488;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0;
  margin-bottom: 0.4rem;
  text-transform: uppercase;
}

.manual-search {
  align-items: center;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.65rem;
}

.manual-search i {
  color: #64748b;
}

.manual-summary {
  align-items: center;
  color: #64748b;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: space-between;
}

.manual-toc {
  background: #f8fafc;
  border: 1px solid rgba(100, 116, 139, 0.16);
  border-radius: 8px;
  display: grid;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
}

.manual-toc-heading {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: space-between;
}

.manual-toc-kicker {
  color: #0d9488;
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
}

.manual-toc-count {
  background: #ffffff;
  border: 1px solid rgba(13, 148, 136, 0.18);
  border-radius: 999px;
  color: #0f766e;
  font-size: 0.78rem;
  font-weight: 800;
  padding: 0.32rem 0.7rem;
}

.manual-toc-grid {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.manual-toc-group {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 8px;
  display: grid;
  gap: 0.55rem;
  padding: 0.8rem;
}

.manual-toc-group strong {
  color: #0f172a;
  font-size: 0.86rem;
}

.manual-toc-group ol {
  display: grid;
  gap: 0.38rem;
  margin: 0;
  padding-left: 1.1rem;
}

.manual-toc-group li {
  color: #94a3b8;
  padding-left: 0.15rem;
}

.manual-toc-group a {
  color: #475569;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.3;
  text-decoration: none;
}

.manual-toc-group a:hover {
  color: #0d9488;
}

.manual-doc-layout {
  align-items: start;
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
}

.manual-sidebar {
  min-width: 0;
  position: sticky;
  top: 5.5rem;
}

.manual-sidebar-inner {
  background: var(--suite241-card-bg, #ffffff);
  border: 1px solid var(--suite241-card-border, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 0.35rem 1rem rgba(15, 23, 42, 0.06);
  display: grid;
  gap: 0.55rem;
  max-height: calc(100vh - 7rem);
  overflow-y: auto;
  padding: 0.75rem;
}

.manual-sidebar-title {
  color: #0f172a;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0;
  padding: 0 0.2rem;
  text-transform: uppercase;
}

.manual-sidebar nav {
  display: grid;
  gap: 0.25rem;
}

.manual-sidebar-link {
  border-left: 3px solid transparent;
  border-radius: 7px;
  color: #475569;
  display: grid;
  gap: 0.15rem;
  padding: 0.58rem 0.65rem;
  text-decoration: none;
  transition:
    background-color 0.16s ease,
    border-color 0.16s ease,
    color 0.16s ease;
}

.manual-sidebar-link small {
  color: #0f766e;
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.manual-sidebar-link span {
  font-size: 0.84rem;
  font-weight: 800;
  line-height: 1.25;
}

.manual-sidebar-link:hover,
.manual-sidebar-link.active {
  background: rgba(13, 148, 136, 0.08);
  border-left-color: #0d9488;
  color: #0f172a;
}

.manual-content {
  min-width: 0;
}

.manual-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
}

.manual-card {
  border-radius: 8px;
  scroll-margin-top: 6rem;
}

.manual-card .card-body {
  display: grid;
  gap: 0.85rem;
}

.manual-card-header {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: space-between;
}

.manual-module-badge {
  background: rgba(13, 148, 136, 0.12);
  border: 1px solid rgba(13, 148, 136, 0.22);
  border-radius: 999px;
  color: #0f766e;
  font-size: 0.72rem;
  font-weight: 800;
  padding: 0.28rem 0.65rem;
}

.manual-path {
  align-items: flex-start;
  background: #f8fafc;
  border: 1px solid rgba(100, 116, 139, 0.14);
  border-radius: 8px;
  color: #475569;
  display: flex;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
}

.manual-path i {
  color: #0d9488;
  margin-top: 0.15rem;
}

.manual-summary-text,
.manual-section p,
.manual-subsection p {
  color: #334155;
  font-size: 0.94rem;
  line-height: 1.55;
  margin: 0;
}

.manual-section,
.manual-subsections {
  display: grid;
  gap: 0.55rem;
}

.manual-section h3,
.manual-subsection h3 {
  color: #0f172a;
  font-size: 0.98rem;
  font-weight: 900;
  margin: 0;
}

.manual-steps,
.manual-list {
  color: #475569;
  display: grid;
  gap: 0.4rem;
  margin: 0;
  padding-left: 1.2rem;
}

.manual-steps li,
.manual-list li {
  line-height: 1.45;
}

.manual-info-block,
.manual-callout {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 8px;
  padding: 0.75rem;
}

.manual-info-block strong,
.manual-callout strong {
  align-items: center;
  color: #0f172a;
  display: inline-flex;
  font-size: 0.84rem;
  gap: 0.45rem;
  margin-bottom: 0.35rem;
}

.manual-info-block p,
.manual-callout p {
  color: #475569;
  font-size: 0.88rem;
  line-height: 1.5;
  margin: 0;
}

.manual-info-block.scenario {
  background: #f8fffd;
  border-color: rgba(13, 148, 136, 0.16);
}

.manual-info-block.scenario strong i {
  color: #0d9488;
}

.manual-callout-grid {
  display: grid;
  gap: 0.65rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.manual-callout-tip {
  background: #f8fffd;
  border-color: rgba(13, 148, 136, 0.16);
}

.manual-callout-tip strong i {
  color: #0d9488;
}

.manual-callout-warning {
  background: #fffbeb;
  border-color: rgba(245, 158, 11, 0.24);
}

.manual-callout-warning strong i {
  color: #b45309;
}

.manual-callout-know {
  background: #eff6ff;
  border-color: rgba(37, 99, 235, 0.18);
}

.manual-callout-know strong i {
  color: #2563eb;
}

.manual-figures {
  display: grid;
  gap: 0.85rem;
}

.manual-figure {
  display: grid;
  gap: 0.55rem;
  margin: 0;
}

.manual-figure-frame {
  background: #0f172a;
  border: 1px solid rgba(15, 23, 42, 0.14);
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}

.manual-figure img {
  display: block;
  height: auto;
  width: 100%;
}

.manual-figure-marker {
  align-items: center;
  background: #ef4444;
  border: 2px solid #ffffff;
  border-radius: 999px;
  box-shadow: 0 0.25rem 0.75rem rgba(15, 23, 42, 0.22);
  color: #ffffff;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 900;
  height: 1.65rem;
  justify-content: center;
  position: absolute;
  transform: translate(-50%, -50%);
  width: 1.65rem;
}

.manual-figure figcaption {
  color: #475569;
  display: grid;
  gap: 0.2rem;
  font-size: 0.86rem;
}

.manual-figure figcaption strong {
  color: #0f172a;
}

.manual-figure-notes {
  background: #f8fafc;
  border: 1px solid rgba(100, 116, 139, 0.14);
  border-radius: 8px;
  color: #475569;
  display: grid;
  gap: 0.3rem;
  font-size: 0.84rem;
  margin: 0;
  padding: 0.65rem 0.85rem 0.65rem 1.7rem;
}

.manual-empty .card-body {
  align-items: center;
  color: #64748b;
  display: grid;
  gap: 0.35rem;
  justify-items: center;
  padding: 2.5rem 1rem;
  text-align: center;
}

.manual-empty i {
  color: #94a3b8;
  font-size: 2rem;
}

@media (max-width: 767.98px) {
  .manual-hero-content {
    grid-template-columns: 1fr;
  }

  .manual-print-button {
    justify-self: stretch;
  }

  .manual-doc-layout {
    grid-template-columns: 1fr;
  }

  .manual-toc {
    padding: 0.85rem;
  }

  .manual-toc-grid {
    grid-template-columns: 1fr;
  }

  .manual-sidebar {
    position: static;
  }

  .manual-sidebar-inner {
    max-height: none;
    overflow-x: auto;
    overflow-y: hidden;
  }

  .manual-sidebar nav {
    display: flex;
    gap: 0.5rem;
  }

  .manual-sidebar-link {
    flex: 0 0 min(260px, 82vw);
  }

  .manual-grid {
    grid-template-columns: 1fr;
  }
}

@media print {
  .manual-powered-by {
    bottom: 4mm;
    font-size: 8px;
    left: 0;
    position: fixed;
    text-align: center;
    width: 100%;
  }
  :global(body.suite241-printing-usage-manual *) {
    visibility: hidden !important;
  }

  :global(body.suite241-printing-usage-manual .usage-manual-page),
  :global(body.suite241-printing-usage-manual .usage-manual-page *) {
    visibility: visible !important;
  }

  :global(body.suite241-printing-usage-manual .usage-manual-page) {
    background: #ffffff !important;
    color: #0f172a !important;
    left: 0;
    padding: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }

  .manual-hero,
  .manual-toc,
  .manual-card,
  .manual-info-block,
  .manual-callout,
  .manual-figure-notes {
    box-shadow: none !important;
  }

  .manual-doc-layout {
    display: block;
  }

  .manual-sidebar,
  .manual-search,
  .manual-summary,
  .manual-print-button,
  .manual-card-header .btn {
    display: none !important;
  }

  .manual-card {
    break-inside: avoid;
    margin-bottom: 1rem;
  }

  .manual-figure {
    break-inside: avoid;
  }
}
</style>
